import React, { useCallback, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import { ScrollView } from 'devextreme-react';
import { Button } from 'devextreme-react/button';

import { formatMessage } from 'devextreme/localization';


interface MessageFormProps {
}

export interface MessageFormHandle {
    Open: (message: string, messageType: number) => void;
    OpenComplex: (customComponent: React.ReactNode, messageType: number, options?: OpenComplexOptions) => void;
}

export interface OpenComplexOptions {
    /**
     * `true` shrinks the popup to its content (auto height, capped at 85vh) instead of the fixed
     * 600x600 scrolling box. Use it for short structured content such as the publish checklist.
     */
    fitContent?: boolean;
}

export const InformationMessage = 1;
export const WarningMessage = 2;
export const ErrorMessage = 3;

// Open (message texte court, ex. erreurs de validation) affiche un popup compact ajusté au contenu.
// OpenComplex (contenu riche pouvant être long, ex. liste d'usages bloquant une suppression dans
// DataSourceGrid/AgentGrid) garde le grand popup défilant historique, pensé pour ce cas.
type DisplayMode = 'simple' | 'complex';

function titleFor(messageType: number): string {
    switch (messageType) {
        case InformationMessage: return formatMessage("MessageForm_Information");
        case WarningMessage: return formatMessage("MessageForm_Warning");
        case ErrorMessage: return formatMessage("MessageForm_Error");
        default: return "";
    }
}

const MessageForm = forwardRef<MessageFormHandle, MessageFormProps>(({ }, ref) => {

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [displayMode, setDisplayMode] = useState<DisplayMode>('simple');
    const [title, setTitle] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const [content, setContent] = useState<React.ReactNode | null>(null);
    const [fitContent, setFitContent] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        Open(message: string, messageType: number) {
            setDisplayMode('simple');
            setTitle(titleFor(messageType));
            setMessage(message);
            setContent(null);
            setIsOpen(true);
        },

        OpenComplex(customComponent: React.ReactNode, messageType: number, options?: OpenComplexOptions) {
            setDisplayMode('complex');
            setTitle(titleFor(messageType));
            setContent(customComponent);
            setFitContent(options?.fitContent ?? false);
            setIsOpen(true);
        }
    }));

    const closeDialog = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        }
    }, []);

    const close = () => {
        setIsOpen(false);
    }

    if (displayMode === 'simple') {
        return (
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={closeDialog}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={title}
                container=".dx-viewport"
                width={420}
                height="auto"
                maxHeight="80vh"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <ScrollView showScrollbar="onScroll" direction="vertical" height="auto" width="100%">
                        <div style={{ maxHeight: "60vh" }}>{message}</div>
                    </ScrollView>
                    <Button text="Ok" type="default" width="100%" onClick={close} />
                </div>
            </Popup>
        );
    }

    if (fitContent) {
        return (
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={closeDialog}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={title}
                container=".dx-viewport"
                width={480}
                height="auto"
                maxHeight="85vh"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <ScrollView showScrollbar="onScroll" direction="vertical" height="auto" width="100%">
                        <div style={{ maxHeight: "65vh", paddingRight: 8 }}>
                            {content}
                        </div>
                    </ScrollView>
                    <Button text="Ok" type="default" width="100%" onClick={close} />
                </div>
            </Popup>
        );
    }

    return (
        <Popup
            wrapperAttr={popupAttributes}
            visible={isOpen}
            onHiding={closeDialog}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={title}
            container=".dx-viewport"
            width={600}
            height={600}
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100%", }} >
                <div style={{ flex: 1, minHeight: 0 }}>
                    <ScrollView height="100%" showScrollbar="onScroll" direction="vertical" >
                        <div style={{ paddingRight: 8 }}>
                            {content}
                        </div>
                    </ScrollView>
                </div>

                <div style={{ paddingTop: 10, flexShrink: 0 }}>
                    <Button text="Ok" type="default" width="100%" onClick={close}/>
                </div>
            </div>
        </Popup>
    );
});

export default MessageForm;
